import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Gauge, Fuel, Calendar, Cog, Zap, Timer, Car as CarIcon, X, ChevronLeft, ChevronRight, Images, MessageCircle, Heart, Link2, ExternalLink, Image as ImageIcon, RotateCw } from "lucide-react";
import { ListingSellerPhone } from "@/components/ListingSellerPhone";
import { formatPrice, formatKm } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { useFavorites } from "@/context/FavoritesContext";
import { Viewer360 } from "@/components/Viewer360";
import { normalize360Frames, has360Viewer } from "@/lib/viewer360Frames";
import { toast } from "sonner";
import api from "@/lib/api";
import { CarFeaturesDisplay } from "@/components/CarFeaturesDisplay";
import { ListingMap } from "@/components/ListingMap";
import { FavoriteCount } from "@/components/FavoriteCount";
import { carAbsoluteUrl, carPath } from "@/lib/carUrl";
import { TestListingBadge } from "@/components/TestListingBadge";
import { ListingDealerPeek } from "@/components/ListingDealerPeek";

export const QuickView = ({ car, open, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [view, setView] = useState("gallery"); // 'gallery' | '360'
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openChatForCar } = useChat();
  const { isFavorite, toggleFavorite, getFavoriteCount } = useFavorites();
  const [favCount, setFavCount] = useState(car?.favorite_count ?? 0);

  const hasSellerPhone = Boolean((car?.seller_phone || "").trim());

  const gallery = (car?.images && car.images.length > 0) ? car.images : (car ? [car.image] : []);
  const frames360 = normalize360Frames(car?.images_360);
  const has360 = has360Viewer(car);

  useEffect(() => {
    setIdx(0);
    setView("gallery");
    setFavCount(getFavoriteCount(car?.id, car?.favorite_count));
  }, [car?.id, car?.favorite_count, getFavoriteCount]);

  useEffect(() => {
    if (open && car?.id) {
      api.post(`/cars/${car.id}/view`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, car?.id]);

  if (!car) return null;

  const fav = isFavorite(car.id);

  const startChat = async () => {
    if (!user) { onClose(); navigate("/belepes"); return; }
    try {
      await openChatForCar(car);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "A beszélgetés nem indítható el.");
    }
  };

  const copyLink = async () => {
    const url = carAbsoluteUrl(car);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link a vágólapra másolva");
    } catch (_) {
      toast.info(url);
    }
  };

  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + gallery.length) % gallery.length); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % gallery.length); };

  const specs = [
    { icon: Cog, label: "Motor", value: car.engine },
    { icon: Zap, label: "Teljesítmény", value: `${car.horsepower} LE` },
    { icon: Timer, label: "Gyorsulás (0-100)", value: `${car.acceleration} mp` },
    { icon: Fuel, label: "Üzemanyag", value: car.fuel },
    { icon: Gauge, label: "Váltó", value: car.transmission },
    { icon: Calendar, label: "Évjárat", value: car.year },
    { icon: CarIcon, label: "Karosszéria", value: car.body_type },
    { icon: Gauge, label: "Futott km", value: formatKm(car.mileage) },
  ].concat(car.color ? [{ icon: CarIcon, label: "Szín", value: car.color }] : []);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="quick-view-modal"
        className="w-[calc(100vw-1.5rem)] max-w-3xl max-h-[90vh] overflow-y-auto bg-anthracite-800 border border-white/10 text-white p-0 gap-0 rounded-2xl"
      >
        <DialogTitle className="sr-only">{car.make} {car.model} részletek</DialogTitle>
        <DialogDescription className="sr-only">Autó specifikációk és fotógaléria</DialogDescription>
        <div className="relative aspect-[16/9] max-h-[42vh] w-full group/gallery bg-anthracite-900">
          {has360 && view === "360" ? (
            <Viewer360 frames={frames360} className="!rounded-none" />
          ) : (
            <>
              {gallery.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${car.make} ${car.model} – ${i + 1}. nézet`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                    i === idx ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-anthracite-800 to-transparent pointer-events-none" />
            </>
          )}

          {has360 && (
            <div
              data-testid="quickview-view-toggle"
              className="absolute top-4 left-4 z-20 inline-flex items-center gap-1 p-0.5 rounded-full bg-black/55 backdrop-blur-md"
            >
              <button
                data-testid="quickview-view-gallery"
                onClick={(e) => { e.stopPropagation(); setView("gallery"); }}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${view === "gallery" ? "bg-electric text-white" : "text-slate-200 hover:text-white"}`}
              >
                <ImageIcon className="w-3 h-3" strokeWidth={1.75} /> Galéria
              </button>
              <button
                data-testid="quickview-view-360"
                onClick={(e) => { e.stopPropagation(); setView("360"); }}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${view === "360" ? "bg-electric text-white" : "text-slate-200 hover:text-white"}`}
              >
                <RotateCw className="w-3 h-3" strokeWidth={1.75} /> 360°
              </button>
            </div>
          )}
          <button
            data-testid="quick-view-favorite"
            onClick={async () => {
              const res = await toggleFavorite(car);
              if (!res) return;
              setFavCount(res.favorite_count);
              toast.success(res.favorited ? "Elmentve a kedvencek közé" : "Eltávolítva a kedvencek közül");
            }}
            className={`absolute top-4 right-14 z-10 p-2 rounded-full glass-strong transition-all ${fav ? "text-red-400" : "text-slate-300 hover:text-white"}`}
          >
            <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} strokeWidth={1.5} />
          </button>
          <button
            data-testid="quick-view-close"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full glass-strong hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </button>

          {view === "gallery" && gallery.length > 1 && (
            <>
              <button
                data-testid="gallery-prev"
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full glass-strong hover:bg-electric text-white transition-all opacity-0 group-hover/gallery:opacity-100"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <button
                data-testid="gallery-next"
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full glass-strong hover:bg-electric text-white transition-all opacity-0 group-hover/gallery:opacity-100"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
              </button>

              <div className={`absolute z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass-strong text-xs ${has360 ? "top-16 left-4" : "top-4 left-4"}`}>
                <Images className="w-3.5 h-3.5 text-electric" strokeWidth={1.5} />
                <span data-testid="gallery-counter">{idx + 1} / {gallery.length}</span>
              </div>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-dot-${i}`}
                    onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx ? "w-6 bg-electric" : "w-1.5 bg-white/40 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {view === "gallery" && gallery.length > 1 && (
          <div className="flex gap-2 px-6 md:px-8 pt-4 overflow-x-auto">
            {gallery.map((src, i) => (
              <button
                key={i}
                data-testid={`gallery-thumb-${i}`}
                onClick={() => setIdx(i)}
                className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                  i === idx ? "border-electric" : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                <img src={src} alt={`nézet ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-4 sm:p-6 md:p-8 pt-4 sm:pt-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs tracking-[0.15em] uppercase text-slate-400">{car.make} · {car.year}</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-light mt-1">{car.model}</h2>
              {car.is_test_listing && (
                <div className="mt-2">
                  <TestListingBadge />
                </div>
              )}
            </div>
            <div className="font-heading text-2xl sm:text-3xl font-light text-electric">{formatPrice(car.price)}</div>
          </div>

          {car.description && (
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">{car.description}</p>
          )}

          <ListingDealerPeek car={car} open={open} onNavigateAway={onClose} testid="quick-view-dealer-peek" />

          {(car.address || car.city || car.county) && (
            <div className="mt-4">
              <ListingMap car={car} />
            </div>
          )}

          <FavoriteCount count={favCount} testid="quick-view-favorite-count" className="mt-3" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {specs.map((s) => (
              <div key={s.label} className="glass rounded-xl p-3">
                <s.icon className="w-4 h-4 text-electric mb-2" strokeWidth={1.5} />
                <div className="text-[10px] tracking-wide uppercase text-slate-400">{s.label}</div>
                <div className="text-sm font-medium mt-0.5">{s.value}</div>
              </div>
            ))}
          </div>

          <CarFeaturesDisplay features={car.features} compact />

          <div className={`mt-6 grid gap-3 ${hasSellerPhone ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
            <button
              data-testid="car-interest-btn"
              onClick={startChat}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-electric hover:bg-electric-light text-white font-medium transition-all hover:blue-glow"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Érdekel – írok az eladónak
            </button>
            <ListingSellerPhone car={car} testid="quick-view-seller-phone" />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <button
              data-testid="quick-view-full-page"
              onClick={() => { onClose(); navigate(carPath(car)); }}
              className="inline-flex items-center gap-1.5 text-electric hover:text-electric-light transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /> Teljes adatlap megnyitása
            </button>
            <button
              data-testid="quick-view-copy-link"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
            >
              <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Megosztható link másolása
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
